import { useCallback, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import SetCurrencyButton from '../multicurrency/SetCurrencyButton';
import MulticurrencyPanel from '../multicurrency/MulticurrencyPanel';
import { createNewGoalsUrl, getReturnLink } from '../../utils/urls';
import { trackCreateInAppGoal, trackEditGoalsFromForecast, trackOpenManageTabFromForecast, trackViewReportFromForecast } from '../../utils/usageTracker';
import Links from '../../constants/Links';
import { useSelector } from 'react-redux';
import { getHasSuperAdminScope } from '../../selectors/authSelectors';
import { getIsDealStageView, getIsWeightedAmountPortalConfig, getSelectedForecastTypeIsDefault } from '../../selectors/forecastSelectors';
import { getIsUngatedToForecastingMultipleOutcomes } from '../../selectors/authSelectors';
import { FORECAST_CATEGORY } from '../../constants/DealGroupingType';
import { getSelectedTab } from '../../selectors/tabsSelectors';
import { getDealGroupingTypeFilter } from '../../selectors/filtersSelectors';
import { getCurrencyCountGreaterThanOne } from '../../selectors/multiCurrencySelectors';
import { ANALYZE } from '../../constants/Tabs';
import { CreateForecastButton } from '../fmo/CreateForecastButton';
import CreateForecastTourStep from '../tour/fmo/CreateForecastTourStep';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const HeaderButtons = ({
  isDetail
}) => {
  const dealGroupingType = useSelector(getDealGroupingTypeFilter);
  const isWeightedAmountPortalConfig = useSelector(getIsWeightedAmountPortalConfig);
  const isDealStageView = useSelector(getIsDealStageView);
  const isDefaultForecast = useSelector(getSelectedForecastTypeIsDefault);
  const selectedTab = useSelector(getSelectedTab);
  const currencyCountGreaterThanOne = useSelector(getCurrencyCountGreaterThanOne);
  const isUngatedToFMO = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  const hasSuperAdminScope = useSelector(getHasSuperAdminScope);
  const [isMultiCurrencyPanelOpen, setIsMultiCurrencyPanelOpen] = useState(false);

  // Also used for ANALYZE tab
  let reportHref = Links.SALES_REPORTS_WEIGHTED_PIPELINE;
  if (dealGroupingType === FORECAST_CATEGORY) {
    reportHref = isWeightedAmountPortalConfig ? Links.SALES_REPORTS_WEIGHTED : Links.SALES_REPORTS;
  }
  const showSetCurrencyButtonInHeader = !isUngatedToFMO && currencyCountGreaterThanOne && selectedTab !== ANALYZE;
  const showSetCurrencyButtonInActions = isUngatedToFMO && currencyCountGreaterThanOne && selectedTab !== ANALYZE;
  const showCreateButton = isUngatedToFMO && selectedTab !== ANALYZE;
  const showManageForecastsButton = isUngatedToFMO && hasSuperAdminScope;
  const onClickGoToSalesAnalytics = useCallback(() => {
    trackViewReportFromForecast({
      isDealStageView,
      isDetail
    });
  }, [isDealStageView, isDetail]);
  const onClickEditGoals = useCallback(() => {
    trackEditGoalsFromForecast({
      isDealStageView,
      isDetail
    });
  }, [isDealStageView, isDetail]);
  const onClickManageForecasts = useCallback(() => {
    trackOpenManageTabFromForecast({
      isDealStageView,
      isDetail
    });
  }, [isDealStageView, isDetail]);
  const onCloseMulticurrencyEditPanel = useCallback(() => setIsMultiCurrencyPanelOpen(false), []);
  const onClickCreateGoal = useCallback(() => {
    trackCreateInAppGoal({
      isDealStageView,
      isDefaultForecast
    });
  }, [isDealStageView, isDefaultForecast]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIDropdown, {
      buttonSize: 'small',
      buttonUse: "secondary",
      buttonText: I18n.text('index.header.actions.actionsTitle'),
      "data-test-id": "actions-dropdown",
      children: /*#__PURE__*/_jsxs(UIList, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          size: "small",
          href: reportHref,
          external: true,
          onClick: onClickGoToSalesAnalytics,
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "index.header.actions.goToSalesAnalytics"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          size: "small",
          href: Links.GOALS,
          onClick: onClickEditGoals,
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "index.header.actions.editGoals"
          })
        }), showSetCurrencyButtonInActions && /*#__PURE__*/_jsx(SetCurrencyButton, {
          isDetail: isDetail,
          isUngatedToFMO: isUngatedToFMO,
          setIsMultiCurrencyPanelOpen: setIsMultiCurrencyPanelOpen
        }), showManageForecastsButton && /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "manage-forecasts-button",
          external: true,
          href: getReturnLink(window.location.pathname, window.location.search, Links.SETTINGS_MANAGE),
          onClick: onClickManageForecasts,
          size: "small",
          use: "secondary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "index.header.actions.manageForecasts"
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(_Fragment, {
      children: [showSetCurrencyButtonInHeader && /*#__PURE__*/_jsx(SetCurrencyButton, {
        isDetail: isDetail,
        isUngatedToFMO: isUngatedToFMO,
        setIsMultiCurrencyPanelOpen: setIsMultiCurrencyPanelOpen
      }), isMultiCurrencyPanelOpen && /*#__PURE__*/_jsx(MulticurrencyPanel, {
        isDetail: isDetail,
        onClose: onCloseMulticurrencyEditPanel
      })]
    }), showCreateButton && /*#__PURE__*/_jsx(CreateForecastTourStep, {
      children: /*#__PURE__*/_jsx(CreateForecastButton, {
        buttonLocation: "header"
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      size: "small",
      use: "primary",
      href: createNewGoalsUrl(),
      onClick: onClickCreateGoal,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "index.header.actions.createGoal"
      })
    })]
  });
};
export default HeaderButtons;